import {
    DeleteOutline,
    FolderOpenOutlined,
    ImageOutlined,
    PictureAsPdfOutlined,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import { JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { parseApiError } from '../../../library/apiErrorHelper';
import { openBlobInNewTab } from '../../../library/blobHelper';
import { formatRecordDate } from '../../../library/dateTimeHelper';
import {
    ACCEPTED_DOCUMENT_TYPES,
    canRemoveDocument,
    categoryLabelKey,
    IMedicalRecordItem,
    recordKey,
} from '../../../library/models/medicalRecord';
import { useIsCompact } from '../../../library/useIsCompact';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactInfiniteQuery, useReactMutation } from '../../../services/queryService';
import { services } from '../../../services/services';
import { ConfirmationDialog } from '../../confirmationDialog/confirmationDialog';
import { useSnackbar } from '../../layout/snackbarProvider';
import { RecordCard } from '../../recordCard/RecordCard';
import { cardListStyles } from '../../recordCard/recordCard.styles';
import {
    alertStyles,
    emptyStateBoxStyles,
    emptyStateIconStyles,
    loadingBoxStyles,
    subtitleStyles,
} from './patientDetail.styles';
import {
    authorCellStyles,
    dateCellStyles,
    descriptionCardValueStyles,
    descriptionCellStyles,
    descriptionDetailStyles,
    descriptionTextStyles,
    headerCellStyles,
    rowActionsStyles,
    showMoreBoxStyles,
    tableContainerStyles,
} from './medicalRecordsTimeline.styles';

/**
 * How many arrive at once, and how many each "show more" adds.
 *
 * Five rather than a screenful: what a therapist opening a patient wants is the last
 * sheet or two, and the rest is history they go looking for deliberately.
 */
const RECORDS_PAGE_SIZE = 5;

/** An evaluation's document is always a PDF; an uploaded one is whatever the list says. */
const PDF_ONLY: string[] = ['application/pdf'];

interface IMedicalRecordsTimelineProps {
    patientId: number;
}

/**
 * This patient's record, on the page their file is read from: the evaluations taken here
 * and the documents they brought in, as one list, newest first by the date on each.
 *
 * One list rather than two because that is how a file is read -- the MRI report belongs
 * between the visit that asked for it and the one that acted on it, not in a separate pile.
 * A document sorts by the date *on* it, so a scan from March uploaded in September sits in
 * March.
 *
 * Paged against the server, never sliced, and merged there too: two paged lists cannot be
 * merged in the browser without fetching all of both.
 *
 * Read-only. An evaluation is a dated record of what was said on the day, and a document is
 * what somebody else wrote; correcting either means adding another. The exception is
 * removing one that should not be here at all -- a sheet or a file put against the wrong
 * patient. Both are soft on the server: the line leaves this list and the record is kept.
 * **Who may is not the same for the two.** A sheet is an administrator's to withdraw. A
 * document is too, and also its uploader's for a day -- see `canRemoveDocument`.
 */
export const MedicalRecordsTimeline = ({ patientId }: IMedicalRecordsTimelineProps): JSX.Element => {
    const theme = useTheme();
    const { t: translate, i18n } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const isCompact = useIsCompact();

    const { user, role } = useAuth();
    const isAdmin = role === 'ADMIN';

    /** `recordKey`s, since an evaluation and a document may share an id. */
    const [openingKey, setOpeningKey] = useState<string | undefined>(undefined);
    const [deleting, setDeleting] = useState<IMedicalRecordItem | undefined>(undefined);

    const deleteEvaluation = useReactMutation(services.evaluations.deleteEvaluationMutation());
    const deleteDocument = useReactMutation(services.medicalRecords.deleteDocumentMutation());
    const isDeleting = deleteEvaluation.isPending || deleteDocument.isPending;

    const {
        data,
        isLoading,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        refetch,
    } = useReactInfiniteQuery<IMedicalRecordItem>(
        services.medicalRecords.getTimeline(patientId, RECORDS_PAGE_SIZE),
        // A non-numeric id in the URL would otherwise become "?patientId=NaN".
        { enabled: Number.isFinite(patientId) }
    );

    // The same distinction the rest of this page draws: a query can settle without data
    // and without an error, and reading that as "nothing on record" would be a lie.
    const hasLoaded = data !== undefined;
    const records = useMemo(
        () => data?.pages.flatMap(page => page.items) ?? [],
        [data]
    );

    /** Every page reports the same total, so the first one answers for all of them. */
    const totalItems = data?.pages[0]?.totalItems ?? 0;
    const remaining = Math.max(totalItems - records.length, 0);

    const isEvaluation = (item: IMedicalRecordItem): boolean => item.kind === 'EVALUATION';

    /**
     * Fetches the file as a blob and hands it to the browser, rather than pointing an
     * anchor at the API. The endpoint needs the bearer token, which a plain link cannot
     * carry. `openBlobInNewTab` refuses anything that is not the type this line promised.
     */
    const open = async (item: IMedicalRecordItem): Promise<void> => {
        const errorKey = isEvaluation(item)
            ? ResourcesIds.EVALUATION_PDF_ERROR
            : ResourcesIds.MEDICAL_DOCUMENT_OPEN_ERROR;

        setOpeningKey(recordKey(item));

        try {
            const blob = isEvaluation(item)
                ? await services.evaluations.fetchEvaluationPdf(item.id)
                : await services.medicalRecords.fetchDocumentFile(item.id);

            if (!openBlobInNewTab(blob, isEvaluation(item) ? PDF_ONLY : ACCEPTED_DOCUMENT_TYPES)) {
                showSnackbar(translate(errorKey), 'error');
            }
        } catch {
            showSnackbar(translate(errorKey), 'error');
        } finally {
            setOpeningKey(undefined);
        }
    };

    const onDelete = (): void => {
        if (!deleting) {
            return;
        }

        const item = deleting;
        setDeleting(undefined);

        const successKey = isEvaluation(item)
            ? ResourcesIds.EVALUATION_DELETE_SUCCESS
            : ResourcesIds.MEDICAL_DOCUMENT_DELETE_SUCCESS;
        const errorKey = isEvaluation(item)
            ? ResourcesIds.EVALUATION_DELETE_ERROR
            : ResourcesIds.MEDICAL_DOCUMENT_DELETE_ERROR;

        (isEvaluation(item) ? deleteEvaluation : deleteDocument).mutate(item.id, {
            onSuccess: () => {
                showSnackbar(translate(successKey), 'success');
                // Refetched rather than spliced out locally: the pages after this one have
                // shifted by a row and the total has changed, and only the server knows both.
                refetch();
            },
            // The API's own words where there are any: "this one is older; ask an
            // administrator" is something the person can act on.
            onError: (error: unknown) =>
                showSnackbar(parseApiError(error).message ?? translate(errorKey), 'error'),
        });
    };

    /** A sheet is an administrator's to withdraw; a document has a rule of its own. */
    const canDelete = (item: IMedicalRecordItem): boolean =>
        isEvaluation(item) ? isAdmin : canRemoveDocument(item, role, user?.id);

    const typeLabel = (item: IMedicalRecordItem): string =>
        translate(
            item.kind === 'DOCUMENT' && item.category
                ? categoryLabelKey(item.category)
                : ResourcesIds.MEDICAL_RECORD_KIND_EVALUATION
        );

    /** Filled for a sheet and outlined for a document, so the two read apart at a glance. */
    const typeChip = (item: IMedicalRecordItem): JSX.Element => (
        <Chip
            size="small"
            label={typeLabel(item)}
            color={isEvaluation(item) ? 'primary' : 'default'}
            variant={isEvaluation(item) ? 'filled' : 'outlined'}
        />
    );

    const description = (item: IMedicalRecordItem): string =>
        (isEvaluation(item) ? item.reasonForVisit : item.title) ?? '';

    /** A sheet's pain level; the name a document had on the device it came from. */
    const detail = (item: IMedicalRecordItem): string => {
        if (isEvaluation(item)) {
            return item.painLevel === null || item.painLevel === undefined
                ? ''
                : translate(ResourcesIds.MEDICAL_RECORD_PAIN_LEVEL, { level: item.painLevel });
        }

        return item.originalFilename ?? '';
    };

    const openAction = (item: IMedicalRecordItem): JSX.Element => {
        if (isEvaluation(item) && !item.pdfAvailable) {
            return (
                <Typography variant="body2" sx={subtitleStyles}>
                    {translate(ResourcesIds.EVALUATION_PDF_MISSING)}
                </Typography>
            );
        }

        const isImage = !isEvaluation(item) && item.contentType !== 'application/pdf';

        return (
            <Button
                size="small"
                startIcon={isImage ? <ImageOutlined /> : <PictureAsPdfOutlined />}
                disabled={openingKey === recordKey(item)}
                onClick={() => open(item)}
            >
                {translate(isEvaluation(item)
                    ? ResourcesIds.EVALUATION_OPEN_PDF
                    : ResourcesIds.MEDICAL_DOCUMENT_OPEN)}
            </Button>
        );
    };

    /** One per row, and the same in both shapes -- a card is a row with the labels shown. */
    const rowActions = (item: IMedicalRecordItem): JSX.Element => (
        <Box sx={rowActionsStyles}>
            {openAction(item)}
            {canDelete(item) && (
                <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteOutline />}
                    disabled={isDeleting}
                    onClick={() => setDeleting(item)}
                >
                    {translate(isEvaluation(item)
                        ? ResourcesIds.EVALUATION_DELETE
                        : ResourcesIds.MEDICAL_DOCUMENT_DELETE)}
                </Button>
            )}
        </Box>
    );

    if (!isLoading && !hasLoaded) {
        return (
            <Alert severity="error" sx={alertStyles}>
                {translate(ResourcesIds.PATIENT_DETAIL_MEDICAL_RECORDS_LOAD_ERROR)}
            </Alert>
        );
    }

    if (isLoading) {
        return (
            <Box sx={loadingBoxStyles}>
                <CircularProgress size={40} />
            </Box>
        );
    }

    if (records.length === 0) {
        return (
            <Box sx={emptyStateBoxStyles}>
                <FolderOpenOutlined sx={emptyStateIconStyles(theme)} />
                <Typography variant="h6" gutterBottom>
                    {translate(ResourcesIds.PATIENT_DETAIL_MEDICAL_RECORDS_EMPTY_TITLE)}
                </Typography>
                <Typography variant="body2" sx={subtitleStyles}>
                    {translate(ResourcesIds.PATIENT_DETAIL_MEDICAL_RECORDS_EMPTY_SUBTITLE)}
                </Typography>
            </Box>
        );
    }

    return (
        <>
            {isCompact ? (
                <Box sx={cardListStyles}>
                    {records.map(item => (
                        // The date is the title here: on the patient's own page their
                        // name is the heading above, and repeating it on every card
                        // would say nothing about which line this is.
                        <RecordCard
                            key={recordKey(item)}
                            title={formatRecordDate(item.date, i18n)}
                            titleAdornment={typeChip(item)}
                            fields={[
                                {
                                    label: translate(ResourcesIds.MEDICAL_RECORD_COLUMN_DESCRIPTION),
                                    value: (
                                        <Box component="span" sx={descriptionCardValueStyles}>
                                            {description(item)}
                                            {detail(item) && (
                                                <Typography variant="caption" sx={descriptionDetailStyles}>
                                                    {detail(item)}
                                                </Typography>
                                            )}
                                        </Box>
                                    ),
                                },
                                {
                                    label: translate(ResourcesIds.MEDICAL_RECORD_COLUMN_AUTHOR),
                                    value: item.authorName ?? '',
                                },
                            ]}
                            actions={rowActions(item)}
                        />
                    ))}
                </Box>
            ) : (
                <TableContainer component={Paper} sx={tableContainerStyles}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headerCellStyles}>
                                    {translate(ResourcesIds.MEDICAL_RECORD_COLUMN_DATE)}
                                </TableCell>
                                <TableCell sx={headerCellStyles}>
                                    {translate(ResourcesIds.MEDICAL_RECORD_COLUMN_TYPE)}
                                </TableCell>
                                <TableCell sx={headerCellStyles}>
                                    {translate(ResourcesIds.MEDICAL_RECORD_COLUMN_DESCRIPTION)}
                                </TableCell>
                                <TableCell sx={headerCellStyles}>
                                    {translate(ResourcesIds.MEDICAL_RECORD_COLUMN_AUTHOR)}
                                </TableCell>
                                <TableCell sx={headerCellStyles}>
                                    {translate(ResourcesIds.MEDICAL_RECORD_COLUMN_ACTIONS)}
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {records.map(item => (
                                <TableRow key={recordKey(item)}>
                                    <TableCell sx={dateCellStyles}>
                                        {formatRecordDate(item.date, i18n)}
                                    </TableCell>
                                    <TableCell>{typeChip(item)}</TableCell>
                                    <TableCell sx={descriptionCellStyles}>
                                        <Typography variant="body2" sx={descriptionTextStyles}>
                                            {description(item)}
                                        </Typography>
                                        {detail(item) && (
                                            <Typography variant="caption" sx={descriptionDetailStyles}>
                                                {detail(item)}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={authorCellStyles}>{item.authorName ?? ''}</TableCell>
                                    <TableCell>{rowActions(item)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Drives both shapes, so a phone and a laptop are never showing different
                amounts of the same history. */}
            {hasNextPage && (
                <Box sx={showMoreBoxStyles}>
                    <Button
                        disabled={isFetchingNextPage}
                        onClick={() => fetchNextPage()}
                    >
                        {translate(ResourcesIds.PATIENT_DETAIL_MEDICAL_RECORDS_SHOW_MORE, {
                            remaining,
                        })}
                    </Button>
                </Box>
            )}

            <ConfirmationDialog
                isOpen={deleting !== undefined}
                title={translate(deleting && !isEvaluation(deleting)
                    ? ResourcesIds.MEDICAL_DOCUMENT_DELETE_CONFIRM_TITLE
                    : ResourcesIds.EVALUATION_DELETE_CONFIRM_TITLE)}
                description={deleting && !isEvaluation(deleting)
                    ? translate(ResourcesIds.MEDICAL_DOCUMENT_DELETE_CONFIRM_MESSAGE, {
                        title: deleting.title ?? '',
                    })
                    : translate(ResourcesIds.EVALUATION_DELETE_CONFIRM_MESSAGE, {
                        date: deleting ? formatRecordDate(deleting.date, i18n) : '',
                    })}
                confirmMessage={translate(deleting && !isEvaluation(deleting)
                    ? ResourcesIds.MEDICAL_DOCUMENT_DELETE
                    : ResourcesIds.EVALUATION_DELETE)}
                denyMessage={translate(ResourcesIds.CANCEL_TEXT)}
                onConfirm={onDelete}
                onClose={() => setDeleting(undefined)}
            />
        </>
    );
};
